/**
 * Tree Global Variables 도메인 유틸리티 함수
 */

import { capitalize } from 'lodash-es';
import type {
  TreeDepthSelectionMode,
  TreeVariablePathRule,
  TreeVariableRequest,
} from './treeVariable.types';
import {
  TREE_VARIABLE_TAG_TYPE,
  PLATFORM_ACTUAL_OBJECT_TYPE_MAP,
  OBJECT_TYPE_LABEL_MAP,
  PATH_RULE_OPTIONS_BY_OBJECT_TYPE,
  PATH_RULE_DEPTHS,
  OBJECT_TYPE_DEPTHS,
} from './treeVariable.constants';

/** 폼의 objectType(group/list)을 플랫폼별 실제 API 값으로 변환. K8S 등은 그대로 반환. */
export const resolveObjectType = (platform: string, objectType: string): string =>
  PLATFORM_ACTUAL_OBJECT_TYPE_MAP[platform]?.[objectType] ?? objectType;

/**
 * 트리의 leaf 노드 resolved 키 계산.
 * objectType='workload'이고 workloadType이 지정되면 workloadType을, 아니면 objectType을 사용해
 * 플랫폼별 실제 API 값으로 변환한다.
 */
export const resolveLeafKey = (
  platform: string,
  objectType: string,
  workloadType?: string,
): string => {
  const key = objectType === 'workload' && workloadType ? workloadType : objectType;
  return resolveObjectType(platform, key);
};

/** 실제 API 값(host, infra_group 등)을 폼 값(list, group)으로 역변환. K8S 등은 그대로 반환. */
export const unresolveObjectType = (platform: string, actualValue: string): string => {
  const map = PLATFORM_ACTUAL_OBJECT_TYPE_MAP[platform];
  if (!map) return actualValue;
  const entry = Object.entries(map).find(([, v]) => v === actualValue);
  return entry ? entry[0] : actualValue;
};

/** 실제 API 값(infra_group, host 등)을 표시용 label(Group, List 등)로 역변환 */
export const resolveObjectTypeLabel = (platform: string, actualValue: string): string => {
  const map = PLATFORM_ACTUAL_OBJECT_TYPE_MAP[platform];
  if (!map) return capitalize(actualValue);
  const entry = Object.entries(map).find(([, v]) => v === actualValue);
  return entry
    ? (OBJECT_TYPE_LABEL_MAP[entry[0]] ?? capitalize(entry[0]))
    : capitalize(actualValue);
};

export const isPatternTagType = (tagType: string): boolean =>
  tagType === TREE_VARIABLE_TAG_TYPE.PATTERN.OR || tagType === TREE_VARIABLE_TAG_TYPE.PATTERN.AND;

/**
 * objectType(+ pathRule) 기반 depths 객체 계산 (objectType 자신도 마지막 depth로 포함).
 * 결과 값은 항상 raw 폼 키 ('group', 'list', 'cluster', 'workload' 등) — domain/SPEC.md
 * "Raw vs Resolved 키" 약속에 따라 form.depths 와 request body 의 depth1~4 는 raw 로 유지.
 */
export const computeDepths = (
  objectType: string,
  pathRule?: TreeVariablePathRule,
): Pick<TreeVariableRequest, 'depth1' | 'depth2' | 'depth3' | 'depth4'> => {
  if (!objectType) return {};

  const keys: string[] = [];

  // pathRule이 필요한 objectType (pod, container)
  if (objectType in PATH_RULE_OPTIONS_BY_OBJECT_TYPE && pathRule) {
    const parentDepths = PATH_RULE_DEPTHS[pathRule] ?? [];
    keys.push(...parentDepths.map(({ key }) => key));
    if (objectType === 'container') {
      keys.push('pod');
    }
  } else {
    // objectType별 고정 부모 계층 (list, node, namespace, workload 등)
    const depths = OBJECT_TYPE_DEPTHS[objectType];
    if (depths) {
      keys.push(...depths.map(({ key }) => key));
    }
  }

  const result: Record<string, string> = {};
  keys.forEach((key, index) => {
    result[`depth${index + 1}`] = key;
  });
  return result as Pick<TreeVariableRequest, 'depth1' | 'depth2' | 'depth3' | 'depth4'>;
};

/**
 * 부모 depth 의 resolved tagKey 목록을 루트→하위(deeper) 순으로 반환 (leaf 제외).
 * 'workload' 는 workloadType 실제 값으로 압축한 뒤 resolve 하여 depthSelections/depthSelectionModes
 * 의 키 형식과 일치시킨다 (compacted + resolved).
 */
export const getOrderedParentResolvedKeys = (
  platform: string,
  objectType: string,
  pathRule?: TreeVariablePathRule,
  workloadType?: string,
): string[] =>
  (Object.values(computeDepths(objectType, pathRule)).filter(Boolean) as string[])
    .map((raw) => (raw === 'workload' && workloadType ? workloadType : raw))
    .map((compacted) => resolveObjectType(platform, compacted));

/**
 * 부모 depth + leaf 의 resolved tagKey 목록을 shallow(루트) → leaf 순으로 반환.
 * objectType 미선택 시 빈 배열(leaf 빈 문자열이 섞이지 않도록 가드).
 */
export const getOrderedResolvedKeys = (
  platform: string,
  objectType: string,
  pathRule?: TreeVariablePathRule,
  workloadType?: string,
): string[] =>
  objectType
    ? [
        ...getOrderedParentResolvedKeys(platform, objectType, pathRule, workloadType),
        resolveLeafKey(platform, objectType, workloadType),
      ]
    : [];

export interface SelectionModeState {
  key: string;
  effective: TreeDepthSelectionMode;
  locked: boolean;
}

/**
 * 부모 + leaf 각 depth 별 effective 선택 모드와 locked 여부 계산.
 * @param orderedResolvedKeys 부모 + leaf 전체 depth 의 resolved 키 (root→leaf 순)
 * @param ownModes 사용자 지정(own) 모드 맵. 미존재 키 = 'MULTI'
 *
 * - effective = own SINGLE 또는 자기보다 하위(deeper)에 own SINGLE 존재 시 SINGLE
 * - locked = 자기보다 하위(deeper)에 own SINGLE 이 존재하면, 현재 depth 의 own 여부와 무관하게
 *   선택 변경이 effective 결과에 영향을 주지 않으므로 잠근다(라디오 disabled). leaf 는 최하위라 항상 false.
 *
 * own 은 derive 과정에서 변형하지 않으므로, 하위 단일이 풀리면 상위는 own 모드로 자연 복원된다.
 */
export const resolveSelectionModeStates = (
  orderedResolvedKeys: string[],
  ownModes: Record<string, TreeDepthSelectionMode>,
): SelectionModeState[] => {
  const states: SelectionModeState[] = new Array(orderedResolvedKeys.length);
  let deeperHasSingle = false;
  for (let i = orderedResolvedKeys.length - 1; i >= 0; i--) {
    const key = orderedResolvedKeys[i];
    const own = ownModes[key] === 'SINGLE';
    states[i] = {
      key,
      effective: own || deeperHasSingle ? 'SINGLE' : 'MULTI',
      locked: deeperHasSingle,
    };
    if (own) deeperHasSingle = true;
  }
  return states;
};

/**
 * own 이 SINGLE 인 depth 키만 추출(루트→하위 순서 유지).
 * orderedResolvedKeys 에 없는 stale 키는 자연 제외된다.
 */
export const normalizeOwnSingleDepths = (
  orderedResolvedKeys: string[],
  ownModes: Record<string, TreeDepthSelectionMode>,
): string[] => orderedResolvedKeys.filter((key) => ownModes[key] === 'SINGLE');
